var _effectorFileName = "/src/shared/components/ui/button.tsx";
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/lib/cn';
import { Loader } from './loader';
const buttonVariants = cva('inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50', {
  variants: {
    variant: {
      default: 'bg-primary text-primary-foreground shadow hover:bg-primary/90',
      destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
      outline: 'border border-input bg-transparent shadow-sm hover:bg-accent hover:text-accent-foreground',
      secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
      ghost: 'hover:bg-accent hover:text-accent-foreground',
      link: 'text-primary underline-offset-4 hover:underline decoration-brand-accent-normal',
      cta: 'bg-brand-accent-dark hover:bg-brand-accent-black focus:bg-brand-accent-black hover:ring-2 hover:ring-offset-2 hover:ring-brand-accent-black dark:hover:ring-offset-black dark:hover:ring-brand-accent-normal text-white shadow group relative transform-gpu gap-2 overflow-hidden tracking-tighter shadow ring-offset-current transition-all duration-300 ease-out focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 whitespace-normal'
    },
    size: {
      default: 'py-4 xss:h-9 xss:py-0 px-4 py-2',
      sm: 'h-8 rounded-md px-3 text-xs',
      lg: 'h-10 rounded-md px-8',
      icon: 'h-9 w-9',
      'icon-xs': 'h-6 w-6',
      'icon-sm': 'h-8 w-8',
      'icon-lg': 'h-10 w-10',
      link: 'py-2',
      'link-sm': 'py-0'
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'default'
  }
});
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  className,
  variant,
  loading = false,
  disabled,
  size,
  asChild = false,
  children,
  ...props
}, ref) => {
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn('!flex items-center justify-center', buttonVariants({
    variant,
    size,
    className
  }))} ref={ref} disabled={loading || disabled} {...props}>
        {loading && <Loader className="mr-1" />}

        {variant === 'cta' && <span className="absolute right-0 -mt-12 h-32 w-8 translate-x-12 rotate-12 transform-gpu bg-white opacity-10 transition-all duration-1000 ease-out group-hover:-translate-x-96 dark:bg-black"></span>}
        {children}
      </Comp>;
});
Button.displayName = 'Button';
export { Button, buttonVariants };